<template>
  <BaseCard :heading="$t('customers.tags.heading')">
    <CreateTags
      v-if="showCreateModal"
      @close="showCreateModal = false"
      @save="handleSave"
      :tags="tags"
    />
    <div :class="$style.tags">
      <BaseChip
        v-for="(tag, index) in sortedTags"
        :key="index"
        :text="tag.name"
        icon="plus"
        :state="modelValue.includes(tag.id) ? 'selected' : undefined"
        @click="toggleTag(tag)"
        v-test="'tag'"
      />
    </div>
    <div>
      <BaseText
        @click="showCreateModal = true"
        v-test="'manage-tags'"
      >
        {{ tags.length > 1 ? $t('customers.tags.manage_tags') : $t('customers.tags.create_tag') }}
      </BaseText>
    </div>
  </BaseCard>
</template>

<script
  lang="ts"
  setup
>
import { useMutation, useQuery } from '@vue/apollo-composable';
import { useI18n } from 'vue-i18n';
import { flash } from '@/helpers/ui';
import CreateTags from './CreateTags.vue';
import { ASSIGN_CUSTOMER_TAGS, GET_CUSTOMER_TAGS } from './graphql';

type Tag = {
  id: number;
  name: string;
  salonized: boolean;
};

const props = defineProps<{
  modelValue: Tag['id'][];
  saveImmediately?: boolean;
}>();

const emit = defineEmits(['update:modelValue']);

const showCreateModal = ref(false);
const customer = inject<any>('customer');
const tags = ref<Tag[]>([]);

const setTags = (data: any) => {
  if (data.customerTags) {
    tags.value = [
      ...data.customerTags.salonizedTags,
      ...data.customerTags.companyTags,
    ];
  }
};

const { onResult } = useQuery(GET_CUSTOMER_TAGS, {
  fetchPolicy: 'cache-first',
});

onResult(({ data }) => setTags(data));

const handleSave = () => {
  showCreateModal.value = false;
  const { onResult } = useQuery(GET_CUSTOMER_TAGS, {
    fetchPolicy: 'cache-first',
  });

  onResult(({ data }) => setTags(data));
};

const { t } = useI18n();

const sortedTags = computed(() =>
  tags.value.sort((a) => (props.modelValue.includes(a.id) ? 1 : 0)),
);

const toggleTag = (tag: Tag) => {
  let newTags: Tag['id'][] = [];

  if (props.modelValue.includes(tag.id)) {
    newTags = props.modelValue.filter((id) => id !== tag.id);
  } else {
    newTags = [...props.modelValue, tag.id];
  }

  emit('update:modelValue', newTags);

  if (props.saveImmediately) {
    updateTags(newTags);
  }
};

const updateTags = (tagIds: Tag['id'][]) => {
  const { mutate } = useMutation(ASSIGN_CUSTOMER_TAGS);

  mutate({
    input: {
      customerId: customer.value?.id || customer.id,
      tagIds,
    },
  }).then((result) => {
    if (result?.data) {
      const {
        assignCustomerTags: { customerTags },
      } = result.data;
      customer.value.customerTags = customerTags;
      flash(t('global.flash.customer_updated'));
    }
  });
};
</script>

<style
  lang="scss"
  module
>
.tags {
  display: flex;
  flex-wrap: wrap;
  gap: $spacing * 0.5;
  margin-bottom: $spacing;
}
</style>
